/**
 * FlexHR live notification mapping (WebSocket → OS toast).
 * Replaces the old OneSignal tag filters with WS audiences.
 *
 * Persist helpers (POST /notifications → create Lambda → WS) live in
 * sendNotification.js. This module formats live WS event types that do not
 * go through request: "notification" by themselves (clock + tickets).
 */

import dayjsTZ from "./dayjs";

function employeeLabelFromClockBody(body) {
  if (!body || typeof body !== "object") return "Punonjësi";
  const fromLog = body?.clockingLogs?.[0]?.username;
  const name =
    body.username ||
    body.employeeName ||
    fromLog ||
    [body.employeeFirstName || body.given_name, body.employeeLastName || body.family_name]
      .filter(Boolean)
      .join(" ")
      .trim();
  return name || "Punonjësi";
}

/**
 * Admin-facing clock in/out OS toast copy.
 * @returns {{ title: string, message: string, url: string, tag: string } | null}
 */
export function mapClockEventToNotification(request, body, id) {
  const name = employeeLabelFromClockBody(body);
  const empId = body?.employeeId || id || "clock";
  if (request === "user_clockIn_received") {
    const when = body?.clockInDate
      ? dayjsTZ(body.clockInDate).format("HH:mm")
      : dayjsTZ().format("HH:mm");
    return {
      title: "Filloi Turni",
      message: `${name} filloi turnin në ${when}`,
      url: "/aprovimet",
      tag: `clock-in-${empId}-${body?.clockingId || body?.clockInDate || Date.now()}`,
    };
  }
  if (request === "user_clockOut_received") {
    const when = body?.clockOutDate
      ? dayjsTZ(body.clockOutDate).format("HH:mm")
      : dayjsTZ().format("HH:mm");
    return {
      title: "Mbaroi Turni",
      message: `${name} mbaroi turnin në ${when}`,
      url: "/aprovimet",
      tag: `clock-out-${empId}-${body?.clockingId || body?.clockOutDate || Date.now()}`,
    };
  }
  return null;
}

/**
 * Support ticket live OS toast for admins / ticket owner.
 * Persist + history still use sendNotification helpers; OS toast is driven here
 * to avoid double toasts with ticket_* + notification fan-out.
 *
 * @returns {{ title: string, message: string, url: string, tag: string } | null}
 */
export function mapTicketEventToNotification(request, body, { authEmployeeId, userRole } = {}) {
  if (!body?.ticketId) return null;

  const isOwner =
    !!body.employeeId && body.employeeId === authEmployeeId;
  const role = String(userRole || "").toLowerCase();
  const isElevated =
    role === "admin" || role === "super admin" || role === "supervisor";
  if (!isElevated && !isOwner) return null;

  const category = body.ticketCategory || "problematikë";
  const preview =
    body.lastMessagePreview ||
    (typeof body.message?.body === "string"
      ? body.message.body.replace(/<[^>]+>/g, " ").trim().slice(0, 120)
      : "") ||
    category;
  const url = `/problematikat?ticket=${body.ticketId}`;
  const authorRole = String(body.message?.authorRole || "").toLowerCase();
  const msgId = body.message?.messageId || body.lastMessageAt || Date.now();

  if (request === "ticket_created") {
    if (!isElevated) return null;
    return {
      title: "U krijua nje Problematike",
      message: `${body.createdBy || "Punonjësi"} krijoi një problematikë: ${category}`,
      url,
      tag: `ticket-created-${body.ticketId}`,
    };
  }

  if (request === "ticket_message") {
    if (isElevated && (authorRole === "employee" || !authorRole)) {
      return {
        title: "Përgjigje e re në Support",
        message: `${body.createdBy || body.message?.authorName || "Punonjësi"}: ${preview}`,
        url,
        tag: `ticket-msg-${body.ticketId}-${msgId}`,
      };
    }
    if (isOwner && (authorRole === "admin" || authorRole === "super admin")) {
      return {
        title: "Përgjigje nga Support",
        message: `Përgjigje e re për: ${category}`,
        url,
        tag: `ticket-msg-${body.ticketId}-${msgId}`,
      };
    }
    return null;
  }

  if (request === "ticket_updated") {
    if (
      isOwner &&
      String(body.ticketStatus || "").toLowerCase() === "completed"
    ) {
      return {
        title: "U Zgjidh Problematika",
        message: `Problematika "${category}" u shënua si e zgjidhur`,
        url,
        tag: `ticket-done-${body.ticketId}`,
      };
    }
    return null;
  }

  return null;
}

/** Persist helpers that already fan out request: "notification" via API/WS. */
export const PERSISTED_NOTIFICATION_HELPERS = {
  adminOnly: "notificationUserToAdmin",
  adminOnlyWs: "notificationUserToAdminNoDbPost",
  oneEmployee: "notificationAdminToOnlyOneUser | sendNotificationToEmployee",
  adminAndEmployee: "notificationToAllAdminAndSecifikUser",
  departmentWs: "notificationNoPostToDepartment",
  everyone: "notificationAdminToEveryone",
};

/**
 * Support rows also emit ticket_* WS — suppress OS toast on the duplicate
 * `notification` envelope so the user only sees one OS popup.
 */
export function isSupportNotificationDuplicate(body = {}) {
  const type = String(body.notificationType || body.entityType || "").toLowerCase();
  if (type === "support_ticket") return true;
  const url = String(body.url || body.notificationUrl || "");
  if (url.includes("/problematikat")) return true;
  if (body.ticketId || body?.data?.ticketId) return true;
  return false;
}
